import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://michalchamrad.com').replace(/\/$/, '');
const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/me_new.JPG`;

const PAGE_META: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'Michal Chamrád | Portfolio',
    description:
      'Michal Chamrád — entrepreneur and IT analyst building digital ventures and cooperative structures across Europe. Currently: Siella.fi, Erasmus for Young Entrepreneurs.',
  },
  '/about': {
    title: 'About | Michal Chamrád',
    description: 'Background, experience and focus — business leadership, IT and sustainable development.',
  },
  '/education': {
    title: 'Education | Michal Chamrád',
    description: 'Education and academic path — Czech University of Life Sciences and related experience.',
  },
  '/projects': {
    title: 'Projects | Michal Chamrád',
    description: 'Selected projects: business development, conferences, IT asset management and digital transformation.',
  },
  '/skills': {
    title: 'Expertise | Michal Chamrád',
    description: 'Skills and expertise — automation, Power Platform, analytics and leadership.',
  },
  '/hackathons': {
    title: 'Hackathons | Michal Chamrád',
    description: 'Hackathons and innovation challenges — results and highlights.',
  },
  '/contact': {
    title: 'Contact | Michal Chamrád',
    description: 'Get in touch — portfolio contact and collaboration.',
  },
};

const jsonLdPerson = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'Michal Chamrád',
  url: SITE_URL,
  jobTitle: 'Business Leader & IT Professional',
  sameAs: [] as string[],
};

const SeoHead: React.FC = () => {
  const { pathname } = useLocation();
  const normalized = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  const meta = PAGE_META[normalized] ?? PAGE_META['/'];
  const canonical = `${SITE_URL}${normalized === '/' ? '' : normalized}`;
  const ogUrl = canonical;

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      {normalized === '/' && (
        <script type="application/ld+json">{JSON.stringify(jsonLdPerson)}</script>
      )}
    </Helmet>
  );
};

export default SeoHead;
